// Animator Application Types for Nexus Dashboard

// ============================================================================
// ENUMS & UNION TYPES
// ============================================================================

export type AnimatorType =
  | 'art_director'
  | 'designer'
  | 'rigging'
  | 'post_production'
  | '2d_animation'
  | '3d_animation'
  | 'motion_graphics'
  | 'vfx'
  | 'storyboard'

export type ApplicationStatus = 'pending' | 'scheduled' | 'interviewed' | 'accepted' | 'rejected'

// ============================================================================
// MAIN INTERFACES
// ============================================================================

export interface AnimatorApplication {
  id: string
  firstName: string
  lastName: string
  email: string
  portfolioLinks: string[]
  about?: string
  strongAreas?: string
  animatorTypes: AnimatorType[]
  scheduledStart?: string // ISO timestamp
  scheduledEnd?: string // ISO timestamp
  meetingRoomId?: string
  meetingRoomLink?: string
  status: ApplicationStatus
  confirmationSentAt?: string
  createdAt: string
  updatedAt: string
}

// ============================================================================
// DATABASE ROW TYPE (snake_case, matching Supabase)
// ============================================================================

export interface AnimatorApplicationRow {
  id: string
  first_name: string
  last_name: string
  email: string
  portfolio_links: string[]
  about: string | null
  strong_areas: string | null
  animator_types: string[]
  scheduled_start: string | null
  scheduled_end: string | null
  meeting_room_id: string | null
  meeting_room_link: string | null
  status: ApplicationStatus
  confirmation_sent_at: string | null
  created_at: string
  updated_at: string
}

// ============================================================================
// INTERVIEW TEAM CONFIG
// ============================================================================

export interface InterviewTeamConfig {
  id: string
  configKey: string
  teamMemberIds: string[]
  interviewDuration: number // minutes
  createdAt: string
  updatedAt: string
}

export interface InterviewTeamConfigRow {
  id: string
  config_key: string
  team_member_ids: string[]
  interview_duration: number
  created_at: string
  updated_at: string
}

// ============================================================================
// INPUT TYPES
// ============================================================================

export interface CreateAnimatorApplicationInput {
  firstName: string
  lastName: string
  email: string
  portfolioLinks: string[]
  about?: string
  strongAreas?: string
  animatorTypes: AnimatorType[]
  scheduledStart: string // ISO timestamp
  scheduledEnd: string // ISO timestamp
  timezone: string // Applicant's selected timezone (e.g., 'America/New_York')
}

// ============================================================================
// TYPE CONVERSION FUNCTIONS
// ============================================================================

export function applicationRowToApplication(row: AnimatorApplicationRow): AnimatorApplication {
  return {
    id: row.id,
    firstName: row.first_name,
    lastName: row.last_name,
    email: row.email,
    portfolioLinks: row.portfolio_links,
    about: row.about ?? undefined,
    strongAreas: row.strong_areas ?? undefined,
    animatorTypes: row.animator_types as AnimatorType[],
    scheduledStart: row.scheduled_start ?? undefined,
    scheduledEnd: row.scheduled_end ?? undefined,
    meetingRoomId: row.meeting_room_id ?? undefined,
    meetingRoomLink: row.meeting_room_link ?? undefined,
    status: row.status,
    confirmationSentAt: row.confirmation_sent_at ?? undefined,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  }
}

export function configRowToConfig(row: InterviewTeamConfigRow): InterviewTeamConfig {
  return {
    id: row.id,
    configKey: row.config_key,
    teamMemberIds: row.team_member_ids,
    interviewDuration: row.interview_duration,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  }
}

// ============================================================================
// DISPLAY LABELS
// ============================================================================

export const ANIMATOR_TYPE_LABELS: Record<AnimatorType, string> = {
  art_director: 'Art Director',
  designer: 'Designer',
  rigging: 'Rigging',
  post_production: 'Post Production',
  '2d_animation': '2D Animation',
  '3d_animation': '3D Animation',
  motion_graphics: 'Motion Graphics',
  vfx: 'VFX',
  storyboard: 'Storyboard',
}

export const APPLICATION_STATUS_LABELS: Record<ApplicationStatus, string> = {
  pending: 'Pending',
  scheduled: 'Scheduled',
  interviewed: 'Interviewed',
  accepted: 'Accepted',
  rejected: 'Rejected',
}

export const ANIMATOR_TYPES: AnimatorType[] = [
  'art_director',
  'designer',
  'rigging',
  'post_production',
  '2d_animation',
  '3d_animation',
  'motion_graphics',
  'vfx',
  'storyboard',
]
